import React, { useEffect, useState } from 'react';
import { PiFan } from 'react-icons/pi';
import { FiAlertTriangle, FiCheck, FiClock, FiWifiOff, FiX } from 'react-icons/fi';
import OperationDialog, { OperationStatus } from '../../../components/feedback/OperationDialog';
import { descreverFalhaComando, segundosAteNovaTentativa } from '../utils/erroComando';

// Pontos de tentativa: só depois que a 1ª tentativa falha. Sucesso de
// primeira vai direto ao resultado, sem mostrar tentativas.
const pontosDeTentativa = (progress) => {
  const atual = progress?.tentativaAtual || 1;
  const max = progress?.maxTentativas || 1;
  const estado = progress?.estado;
  if (max <= 1 || !(estado === 'falhou' || atual > 1)) return null;

  return Array.from({ length: max }, (_, i) => {
    const n = i + 1;
    // Só existe tentativa seguinte quando a anterior falhou
    if (n < atual) return 'fail';
    if (n > atual) return 'pending';
    if (estado === 'falhou') return 'fail';
    if (estado === 'sucesso') return 'ok';
    return 'live';
  });
};

const EnviandoStatus = ({ progress }) => {
  const atual = progress?.tentativaAtual || 1;
  const max = progress?.maxTentativas || 1;
  const falhou = progress?.estado === 'falhou';

  let texto = 'Aguardando resposta do exaustor…';
  if (falhou) {
    texto = atual < max ? 'Sem resposta. Tentando de novo…' : 'Sem resposta do exaustor…';
  } else if (atual > 1) {
    texto = `Tentando de novo (${atual} de ${max})…`;
  }

  return (
    <OperationStatus
      tone={falhou ? 'warning' : 'primary'}
      icon={<PiFan />}
      loading
      title="Enviando comando"
      description={texto}
      attempts={pontosDeTentativa(progress)}
    />
  );
};

const ResultadoStatus = ({ resultado, sucessoTitulo, autoCloseMs, onRetry, onBack, onViewStatus }) => {
  // Contagem regressiva do "Tentar de novo" quando o comando foi bloqueado
  // (409/429). Já nasce com o valor certo: o diálogo foca o botão principal
  // no mount, e ele não pode aparecer habilitado nem por um render. Cada
  // novo resultado passa pela fase 'sending', então o componente remonta.
  const [espera, setEspera] = useState(() => segundosAteNovaTentativa(resultado));
  useEffect(() => {
    if (espera <= 0) return undefined;
    const timer = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [espera]);

  if (resultado?.verificado) {
    return (
      <OperationStatus tone="success" icon={<FiCheck />} title={sucessoTitulo} autoCloseMs={autoCloseMs} />
    );
  }

  const falha = descreverFalhaComando(resultado);

  if (falha.classe === 'incerto') {
    return (
      <OperationStatus
        tone="warning"
        icon={<FiAlertTriangle />}
        title="Confira o exaustor"
        description={falha.texto}
        primaryAction={{ label: 'Ver status', onClick: onViewStatus }}
        secondaryAction={{ label: 'Enviar de novo', onClick: onRetry }}
      />
    );
  }

  if (falha.classe === 'bloqueado') {
    return (
      <OperationStatus
        tone="info"
        icon={<FiClock />}
        title="Aguarde um instante"
        description={falha.texto}
        primaryAction={{
          label: espera > 0 ? `Tentar de novo em ${espera}s` : 'Tentar de novo',
          onClick: onRetry,
          disabled: espera > 0
        }}
        secondaryAction={{ label: 'Voltar', onClick: onBack }}
      />
    );
  }

  return (
    <OperationStatus
      tone="danger"
      icon={resultado?.motivo === 'erro_rede' ? <FiWifiOff /> : <FiX />}
      title={falha.titulo || 'Não foi possível enviar'}
      description={falha.texto}
      primaryAction={{ label: 'Tentar de novo', onClick: onRetry }}
      secondaryAction={{ label: 'Voltar', onClick: onBack }}
    />
  );
};

/**
 * Diálogo de comando de exaustor: formulário -> enviando -> resultado, tudo
 * no mesmo OperationDialog. O envio sai direto do formulário (sem modal de
 * confirmação); "Voltar" retorna ao formulário com os dados preservados.
 */
const ComandoDialog = ({
  open,
  phase,
  onClose,
  blocos,
  apartamentos,
  selectedBloco,
  selectedApto,
  selectedAction,
  userName,
  onBlocoChange,
  onAptoChange,
  onActionChange,
  onUserNameChange,
  formError,
  onSubmit,
  exaustorNome,
  progress,
  resultado,
  autoCloseMs,
  onRetry,
  onBack,
  onViewStatus
}) => {
  const formCompleto = Boolean(selectedBloco && selectedApto && selectedAction && userName);
  const verbo = selectedAction === 'on' ? 'Ligar' : 'Desligar';
  const sucessoTitulo = `${exaustorNome} ${selectedAction === 'on' ? 'ligado' : 'desligado'}`;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <OperationDialog open={open} phase={phase} title="Nova Requisição de Controle" onClose={onClose}>
      {phase === 'form' && (
        <form className="operation-form" onSubmit={handleSubmit} noValidate>
          <label className="operation-form__field">
            Bloco/Torre:
            <select
              className="operation-form__control"
              value={selectedBloco}
              onChange={(e) => onBlocoChange(parseInt(e.target.value))}
              data-autofocus
            >
              <option value="">Selecione o Bloco</option>
              {blocos.map((bloco) => (
                <option key={bloco.id} value={bloco.id}>
                  {bloco.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="operation-form__field">
            Apartamento:
            <select
              className="operation-form__control"
              value={selectedApto}
              onChange={(e) => onAptoChange(e.target.value)}
              disabled={!selectedBloco}
            >
              <option value="">Selecione o Apartamento</option>
              {(selectedBloco ? apartamentos[selectedBloco] || [] : []).map((apto) => (
                <option key={apto} value={apto}>
                  {apto}
                </option>
              ))}
            </select>
          </label>
          <label className="operation-form__field">
            Ação:
            <select
              className="operation-form__control"
              value={selectedAction}
              onChange={(e) => onActionChange(e.target.value)}
            >
              <option value="">Selecione a Ação</option>
              <option value="on">Ligar</option>
              <option value="off">Desligar</option>
            </select>
          </label>
          <label className="operation-form__field">
            Nome do solicitante:
            <input
              type="text"
              className="operation-form__control"
              value={userName}
              onChange={(e) => onUserNameChange(e.target.value)}
              placeholder="nome do morador"
            />
          </label>

          {formError && <div className="operation-form__error" role="alert">{formError}</div>}

          <button type="submit" className="operation-form__submit">
            {formCompleto ? `${verbo} ${exaustorNome}` : 'Enviar comando'}
          </button>
        </form>
      )}

      {phase === 'sending' && <EnviandoStatus progress={progress} />}

      {phase === 'result' && (
        <ResultadoStatus
          resultado={resultado}
          sucessoTitulo={sucessoTitulo}
          autoCloseMs={autoCloseMs}
          onRetry={onRetry}
          onBack={onBack}
          onViewStatus={onViewStatus}
        />
      )}
    </OperationDialog>
  );
};

export default ComandoDialog;
